import { styled } from '@linaria/react';
import type { ReactNode } from 'react';
import { Text } from '../../../ui-library/typography/typography';

/**
 * `page`: the full-width Flora view heading (default).
 * `settings-page` / `settings-section`: the compact headings used inside the
 * settings layout, which supplies its own padding; they render `h1` / `h2`.
 */
type AgentViewHeaderVariant = 'page' | 'settings-page' | 'settings-section';

interface AgentViewHeaderProps {
  title: string;
  subtitle?: string;
  rightSlot?: ReactNode;
  sticky?: boolean;
  variant?: AgentViewHeaderVariant;
  /** Shown before the title, e.g. a settings section's icon tile. */
  leading?: ReactNode;
}

/**
 * Renders the shared Flora view heading and optional actions. Enable `sticky`
 * to keep the heading attached to the top of its nearest scrolling container.
 * Use a `settings-*` variant inside the settings layout.
 *
 * @example
 * <AgentViewHeader title="Documents" sticky rightSlot={<Button>Upload</Button>} />
 * <AgentViewHeader variant="settings-page" title="Profile" rightSlot={<Button>Save</Button>} />
 *
 * @param {AgentViewHeaderProps} props - Header content, leading visual, actions, positioning behavior, and variant
 * @returns {JSX.Element} The Flora view header
 */
export function AgentViewHeader({
  title,
  subtitle,
  rightSlot,
  sticky = false,
  variant = 'page',
  leading,
}: AgentViewHeaderProps) {
  return (
    <Header $sticky={sticky} data-variant={variant}>
      {leading}
      <TitleBlock>
        {variant === 'page' && (
          <Text size="large" weight="bold" color="var(--ink-darkest)">
            {title}
          </Text>
        )}
        {variant === 'settings-page' && <PageHeading>{title}</PageHeading>}
        {variant === 'settings-section' && <SectionHeading>{title}</SectionHeading>}
        {subtitle && (
          // Settings headers sit on --sky-lighter, where --ink-lighter is only 4.11:1.
          <Text size="small" color={variant === 'page' ? 'var(--ink-lighter)' : 'var(--ink-light)'}>
            {subtitle}
          </Text>
        )}
      </TitleBlock>
      {rightSlot != null && <RightSlot>{rightSlot}</RightSlot>}
    </Header>
  );
}

const Header = styled.div<{ $sticky: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  position: ${({ $sticky }) => ($sticky ? 'sticky' : 'static')};
  top: 0;
  z-index: ${({ $sticky }) => ($sticky ? 10 : 'auto')};
  margin: ${({ $sticky }) => ($sticky ? '0' : '0 0 32px')};
  flex-shrink: 0;
  padding: ${({ $sticky }) => ($sticky ? '40px 80px 32px' : '0 80px')};
  background: ${({ $sticky }) => ($sticky ? 'var(--base-white)' : 'transparent')};

  &[data-variant='settings-page'],
  &[data-variant='settings-section'] {
    position: static;
    padding: 0;
    background: transparent;
    flex-wrap: wrap;
  }

  &[data-variant='settings-page'] {
    margin: 0 0 24px;
  }

  &[data-variant='settings-section'] {
    margin: 0 0 12px;
  }
`;

const PageHeading = styled.h1`
  margin: 0;
  font-size: var(--font-size-large);
  line-height: var(--line-height-xlarge);
  font-weight: var(--font-weight-semibold);
  color: var(--ink-darkest);
`;

const SectionHeading = styled.h2`
  margin: 0;
  font-size: var(--font-size-medium);
  line-height: var(--line-height-large);
  font-weight: var(--font-weight-semibold);
  color: var(--ink-darkest);
`;

const TitleBlock = styled.div`
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  min-width: 0;
`;

const RightSlot = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

export type { AgentViewHeaderProps, AgentViewHeaderVariant };
